import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getTodayString } from '../utils/dateUtils';
import { useAuth } from './useAuth';

// Fields that should be null instead of empty string (for cross-platform sync)
const NULLABLE_STRING_FIELDS = [
  'waitingFor',
  'delegatedTo',
  'notes',
  'projectId',
  'goalId',
  'context',
  'dueDate',
  'scheduledDate',
  'focusDate',
  'followUpDate',
  'source',
  'sourceLink',
  'sourceChannel',
  'timeBlockId',
  'gcalTimeBlockId',
  'gcalTimeBlockName',
  // New delegation fields
  'delegatedToUid',
  'delegatedToName',
  'delegatedToEmail',
  'delegatedByUid',
  'delegatedByName',
  'delegationStatus',
  'delegationNotes'
];

// Delegation status values
export const DELEGATION_STATUSES = {
  PENDING: 'pending',
  ACKNOWLEDGED: 'acknowledged',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  DECLINED: 'declined'
};

/**
 * Normalize task data to ensure empty strings are converted to null.
 * This prevents cross-platform sync issues where iOS treats "" as truthy
 * but JavaScript treats it as falsy.
 */
function normalizeTaskData(data) {
  const normalized = { ...data };
  for (const field of NULLABLE_STRING_FIELDS) {
    if (normalized[field] === '') {
      normalized[field] = null;
    }
  }
  return normalized;
}

// Computed status values (status is derived from task properties, not stored)
export const TASK_STATUSES = {
  INBOX: 'inbox',      // No date, not focused, not waiting, not someday
  TODAY: 'today',      // scheduledDate === today OR focusedForToday
  SCHEDULED: 'scheduled', // scheduledDate > today
  WAITING: 'waiting',  // Has waitingFor or delegatedTo
  SOMEDAY: 'someday',  // isSomeday flag set
  DONE: 'done'         // Has completedAt
};

// Compute task status from properties (single source of truth)
export function computeStatus(task, today = null) {
  const todayStr = today || getTodayString();

  // Priority order matters - first match wins
  if (task.completedAt) return TASK_STATUSES.DONE;
  if (task.isSomeday) return TASK_STATUSES.SOMEDAY;

  // For tasks delegated TO me (_isDelegatedToMe), don't show as WAITING
  // Instead, let them flow through normal Inbox → Today → Done flow
  // Only the task OWNER sees it as WAITING (they're waiting on the delegate)
  if (!task._isDelegatedToMe) {
    // Check both legacy and new delegation fields
    if (task.waitingFor || task.delegatedTo || task.delegatedToUid || task.delegatedToName) {
      return TASK_STATUSES.WAITING;
    }
  }

  if (task.scheduledDate === todayStr || task.focusedForToday) return TASK_STATUSES.TODAY;
  if (task.scheduledDate && task.scheduledDate > todayStr) return TASK_STATUSES.SCHEDULED;

  return TASK_STATUSES.INBOX;
}

// Helper to check if task is delegated (new or legacy fields)
export function isDelegated(task) {
  return !!(task.delegatedTo || task.delegatedToUid || task.delegatedToName);
}

// Helper to get delegate display name (prefers new fields)
export function getDelegateDisplayName(task) {
  if (task.delegatedToName) return task.delegatedToName;
  if (task.delegatedTo) return task.delegatedTo;
  return null;
}

// Context definitions (GTD contexts)
export const TASK_CONTEXTS = [
  { id: 'slack', label: '@Slack', icon: 'MessageSquare', color: 'text-purple-400' },
  { id: 'email', label: '@Email', icon: 'Mail', color: 'text-blue-400' },
  { id: 'calls', label: '@Calls', icon: 'Phone', color: 'text-green-400' },
  { id: 'computer', label: '@Computer', icon: 'Monitor', color: 'text-cyan-400' },
  { id: 'errands', label: '@Errands', icon: 'MapPin', color: 'text-amber-400' },
  { id: 'home', label: '@Home', icon: 'Home', color: 'text-rose-400' },
  { id: 'office', label: '@Office', icon: 'Building', color: 'text-indigo-400' },
  { id: 'agenda', label: '@Agenda', icon: 'Users', color: 'text-orange-400' }
];

// Energy levels for tasks
export const ENERGY_LEVELS = [
  { id: 'low', label: 'Low Energy', icon: 'Battery' },
  { id: 'medium', label: 'Medium Energy', icon: 'BatteryMedium' },
  { id: 'high', label: 'High Energy', icon: 'BatteryFull' }
];

// Time estimates
export const TIME_ESTIMATES = [
  { id: '5', label: '5 min' },
  { id: '15', label: '15 min' },
  { id: '30', label: '30 min' },
  { id: '60', label: '1 hour' },
  { id: '120', label: '2 hours' },
  { id: '240', label: 'Half day' }
];

export function useTasks() {
  const { user, userProfile } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Tasks are private - show current user's tasks AND tasks delegated to them
    if (!user?.uid) {
      setTasks([]);
      setLoading(false);
      return;
    }

    let myTasks = [];
    let delegatedToMeTasks = [];
    let loadedCount = 0;

    const updateTasks = () => {
      // Merge and dedupe (in case a task is both owned by me and delegated to me)
      const allTasks = [...myTasks, ...delegatedToMeTasks];
      const uniqueTasks = allTasks.filter((task, index, self) =>
        index === self.findIndex(t => t.id === task.id)
      );
      setTasks(uniqueTasks);
      loadedCount++;
      if (loadedCount >= 2) {
        setLoading(false);
      }
    };

    // Query 1: Tasks I own (userId === me)
    const myTasksQuery = query(
      collection(db, 'tasks'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    // Query 2: Tasks delegated to me (delegatedToUid === me)
    const delegatedToMeQuery = query(
      collection(db, 'tasks'),
      where('delegatedToUid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubMyTasks = onSnapshot(
      myTasksQuery,
      (snapshot) => {
        myTasks = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          _isDelegatedToMe: false  // I own this task
        }));
        setError(null);  // Clear error on success
        updateTasks();
      },
      (err) => {
        console.error('Error fetching my tasks:', err);
        setError(err.message);
        loadedCount++;
        if (loadedCount >= 2) setLoading(false);
      }
    );

    const unsubDelegatedToMe = onSnapshot(
      delegatedToMeQuery,
      (snapshot) => {
        delegatedToMeTasks = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          _isDelegatedToMe: true  // This was delegated to me by someone else
        }));
        setError(null);  // Clear error on success
        updateTasks();
      },
      (err) => {
        console.error('Error fetching delegated tasks:', err);
        // Don't fail completely if delegation query fails (might be missing index)
        // Reset delegated tasks to empty to prevent stale data
        delegatedToMeTasks = [];
        loadedCount++;
        if (loadedCount >= 2) setLoading(false);
      }
    );

    return () => {
      unsubMyTasks();
      unsubDelegatedToMe();
    };
  }, [user?.uid]);

  const addTask = async (taskData) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add tasks');
    }

    try {
      const newTask = {
        title: taskData.title || '',
        notes: taskData.notes || '',
        // Status is computed from properties, not stored
        context: taskData.context || null,
        projectId: taskData.projectId || null, // Link to project
        goalId: taskData.goalId || null, // Direct link to goal (if no project)
        timeBlockId: taskData.timeBlockId || null,
        gcalTimeBlockId: taskData.gcalTimeBlockId || null, // Google Calendar event ID
        gcalTimeBlockName: taskData.gcalTimeBlockName || null, // Google Calendar event name
        dueDate: taskData.dueDate || null,
        scheduledDate: taskData.scheduledDate || null,
        completedAt: null,
        userId: user.uid, // Multi-tenant: owner by Firebase UID
        source: taskData.source || 'manual',
        sourceChannel: taskData.sourceChannel || null,
        sourceLink: taskData.sourceLink || null,
        energy: taskData.energy || null,
        timeEstimate: taskData.timeEstimate || null,
        priority: taskData.priority || 'normal', // low, normal, high
        waitingFor: taskData.waitingFor || null,
        tags: taskData.tags || [],
        // Today focus
        focusedForToday: taskData.focusedForToday || false,
        focusDate: taskData.focusDate || null,
        // Legacy delegation & follow-up (for backwards compatibility)
        delegatedTo: taskData.delegatedTo || null,
        delegateDate: taskData.delegateDate || null,
        followUpDate: taskData.followUpDate || null,
        // New unified delegation fields
        delegatedToUid: taskData.delegatedToUid || null,
        delegatedToName: taskData.delegatedToName || null,
        delegatedToEmail: taskData.delegatedToEmail || null,
        delegatedByUid: taskData.delegatedByUid || null,
        delegatedByName: taskData.delegatedByName || null,
        delegatedAt: taskData.delegatedAt || null,
        delegationStatus: taskData.delegationStatus || null,
        delegationNotes: taskData.delegationNotes || null,
        // Someday flag (for computed status)
        isSomeday: taskData.isSomeday || false,
        // Recurring
        isRecurring: taskData.isRecurring || false,
        recurrencePattern: taskData.recurrencePattern || null,
        recurrenceDay: taskData.recurrenceDay || null,
        templateId: taskData.templateId || null,
        lastCreatedDate: taskData.lastCreatedDate || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Normalize empty strings to null for cross-platform compatibility
      const normalizedTask = normalizeTaskData(newTask);
      const docRef = await addDoc(collection(db, 'tasks'), normalizedTask);
      return docRef.id;
    } catch (err) {
      console.error('Error adding task:', err);
      throw err;
    }
  };

  const updateTask = async (id, taskData) => {
    try {
      const taskRef = doc(db, 'tasks', id);
      // Normalize empty strings to null for cross-platform compatibility
      const normalizedData = normalizeTaskData(taskData);
      await updateDoc(taskRef, {
        ...normalizedData,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  };

  const deleteTask = async (id) => {
    try {
      await deleteDoc(doc(db, 'tasks', id));
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  };

  // Complete task (sets completedAt, status computed as DONE)
  const completeTask = async (id) => {
    try {
      const taskRef = doc(db, 'tasks', id);
      await updateDoc(taskRef, {
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error completing task:', err);
      throw err;
    }
  };

  // Uncomplete task (clears completedAt, status recomputed from other properties)
  const uncompleteTask = async (id) => {
    try {
      const taskRef = doc(db, 'tasks', id);
      await updateDoc(taskRef, {
        completedAt: null,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error uncompleting task:', err);
      throw err;
    }
  };

  // Move to Inbox (clear all status-affecting properties)
  const moveToInbox = async (id) => {
    await updateTask(id, {
      scheduledDate: null,
      focusedForToday: false,
      focusDate: null,
      waitingFor: null,
      delegatedTo: null,
      followUpDate: null,
      isSomeday: false,
      // Clear new delegation fields
      delegatedToUid: null,
      delegatedToName: null,
      delegatedToEmail: null,
      delegatedByUid: null,
      delegatedByName: null,
      delegatedAt: null,
      delegationStatus: null,
      delegationNotes: null
    });
  };

  /**
   * Move task to Waiting status
   * Supports both delegation and non-delegation waiting
   *
   * @param {string} id - Task ID
   * @param {string|null} waitingFor - Non-delegation waiting reason (e.g., "vendor response")
   * @param {object|string|null} delegate - Delegate object { uid, name, email, isExternal } or legacy string
   * @param {string|null} followUpDate - Follow-up date in YYYY-MM-DD format
   */
  const moveToWaiting = async (id, waitingFor = '', delegate = null, followUpDate = null) => {
    // Determine if delegate is new format (object) or legacy (string)
    const isNewFormat = delegate && typeof delegate === 'object';
    const isLegacyString = delegate && typeof delegate === 'string';

    if (isNewFormat) {
      // New format: use full delegation system
      const delegatorName = userProfile?.displayName || user?.displayName || user?.email || 'Unknown';

      await updateTask(id, {
        // Clear non-delegation waiting
        waitingFor: null,

        // Set legacy field for backwards compatibility
        delegatedTo: delegate.name,

        // Set new delegation fields
        delegatedToUid: delegate.uid || null,
        delegatedToName: delegate.name,
        delegatedToEmail: delegate.email || null,
        delegatedByUid: user?.uid || null,
        delegatedByName: delegatorName,
        delegatedAt: serverTimestamp(),
        delegationStatus: DELEGATION_STATUSES.PENDING,

        // Set follow-up
        followUpDate: followUpDate || null,
        delegateDate: getTodayString(),

        // Clear conflicting status
        isSomeday: false
      });
    } else if (isLegacyString) {
      // Legacy format: string delegate name
      await updateTask(id, {
        waitingFor: null,
        delegatedTo: delegate,
        followUpDate: followUpDate || null,
        delegateDate: getTodayString(),
        isSomeday: false
      });
    } else {
      // Non-delegation waiting (e.g., "waiting for vendor")
      await updateTask(id, {
        waitingFor: waitingFor || null,
        delegatedTo: null,
        followUpDate: followUpDate || null,
        delegateDate: null,
        // Clear delegation fields
        delegatedToUid: null,
        delegatedToName: null,
        delegatedToEmail: null,
        delegatedByUid: null,
        delegatedAt: null,
        delegationStatus: null,
        isSomeday: false
      });
    }
  };

  // Delegate task using new unified delegation system
  // delegate: { uid?: string, name: string, email?: string, isExternal: boolean }
  const delegateTask = async (id, delegate, options = {}) => {
    if (!user?.uid) throw new Error('User must be logged in to delegate tasks');

    const { followUpDate = null, notes = null } = options;
    const delegatorName = userProfile?.displayName || user?.displayName || user?.email || 'Unknown';

    await updateTask(id, {
      // Clear legacy fields
      waitingFor: null,
      delegatedTo: delegate.name, // Keep for backwards compatibility

      // Set new delegation fields
      delegatedToUid: delegate.uid || null,
      delegatedToName: delegate.name,
      delegatedToEmail: delegate.email || null,
      delegatedByUid: user.uid,
      delegatedByName: delegatorName,
      delegatedAt: serverTimestamp(),
      delegationStatus: DELEGATION_STATUSES.PENDING,
      delegationNotes: notes,
      followUpDate: followUpDate,

      // Clear conflicting status properties
      isSomeday: false
    });
  };

  // Update delegation status (for delegates acknowledging, completing, etc.)
  const updateDelegationStatus = async (id, status) => {
    await updateTask(id, {
      delegationStatus: status
    });
  };

  // Remove delegation (return task to owner)
  const removeDelegation = async (id) => {
    await updateTask(id, {
      waitingFor: null,
      delegatedTo: null,
      delegatedToUid: null,
      delegatedToName: null,
      delegatedToEmail: null,
      delegatedByUid: null,
      delegatedByName: null,
      delegatedAt: null,
      delegationStatus: null,
      delegationNotes: null,
      followUpDate: null
    });
  };

  // Move to Someday (set flag, clear conflicting properties)
  const moveToSomeday = async (id) => {
    await updateTask(id, {
      isSomeday: true,
      scheduledDate: null,
      focusedForToday: false,
      focusDate: null,
      waitingFor: null,
      delegatedTo: null,
      followUpDate: null,
      // Clear new delegation fields
      delegatedToUid: null,
      delegatedToName: null,
      delegatedToEmail: null,
      delegatedByUid: null,
      delegatedByName: null,
      delegatedAt: null,
      delegationStatus: null,
      delegationNotes: null
    });
  };

  // Schedule task for a date (clears someday/waiting)
  const scheduleTask = async (id, scheduledDate, timeBlockId = null) => {
    await updateTask(id, {
      scheduledDate,
      timeBlockId,
      isSomeday: false,
      focusedForToday: false,
      focusDate: null
    });
  };

  // Today focus functions
  const addToToday = async (id) => {
    await updateTask(id, {
      focusedForToday: true,
      focusDate: getTodayString()
    });
  };

  const removeFromToday = async (id) => {
    await updateTask(id, {
      focusedForToday: false,
      focusDate: null
    });
  };

  // Set follow-up date (for delegation)
  const setFollowUp = async (id, followUpDate, delegatedTo = null, delegateDate = null) => {
    const updates = { followUpDate };
    if (delegatedTo !== null) updates.delegatedTo = delegatedTo;
    if (delegateDate !== null) updates.delegateDate = delegateDate;
    await updateTask(id, updates);
  };

  // Filter helpers using computed status
  const getInboxTasks = useCallback(() => {
    const today = getTodayString();
    return tasks.filter(t => computeStatus(t, today) === TASK_STATUSES.INBOX);
  }, [tasks]);

  const getTodayViewTasks = useCallback(() => {
    const today = getTodayString();
    return tasks.filter(t => computeStatus(t, today) === TASK_STATUSES.TODAY);
  }, [tasks]);

  const getWaitingTasks = useCallback(() => {
    const today = getTodayString();
    return tasks.filter(t => computeStatus(t, today) === TASK_STATUSES.WAITING);
  }, [tasks]);

  const getScheduledTasks = useCallback(() => {
    const today = getTodayString();
    return tasks.filter(t => computeStatus(t, today) === TASK_STATUSES.SCHEDULED);
  }, [tasks]);

  const getSomedayTasks = useCallback(() => {
    const today = getTodayString();
    return tasks.filter(t => computeStatus(t, today) === TASK_STATUSES.SOMEDAY);
  }, [tasks]);

  const getCompletedTasks = useCallback(() => {
    const today = getTodayString();
    return tasks.filter(t => computeStatus(t, today) === TASK_STATUSES.DONE);
  }, [tasks]);

  // Get tasks for today view with sections (overdue, today, follow-ups)
  const getTodayTasks = useCallback(() => {
    const today = getTodayString();

    // Overdue: scheduled in the past, not done
    const overdue = tasks.filter(t =>
      t.scheduledDate &&
      t.scheduledDate < today &&
      !t.completedAt
    );

    // Today: scheduled for today OR manually focused for today (but not overdue)
    const todayTasks = tasks.filter(t =>
      !t.completedAt && (
        (t.scheduledDate === today) ||
        (t.focusedForToday === true && (!t.scheduledDate || t.scheduledDate >= today))
      )
    );

    // Follow-ups: followUpDate is today
    const followUps = tasks.filter(t =>
      t.followUpDate === today &&
      !t.completedAt
    );

    return { overdue, today: todayTasks, followUps };
  }, [tasks]);

  // Legacy: Get all tasks that should show in Today (flat list for backwards compatibility)
  const getTodayTasksFlat = useCallback(() => {
    const { overdue, today, followUps } = getTodayTasks();
    return [...overdue, ...today, ...followUps];
  }, [getTodayTasks]);

  // Get tasks by context (actionable tasks only - today or scheduled)
  const getTasksByContext = useCallback((contextId) => {
    const today = getTodayString();
    return tasks.filter(t => {
      const status = computeStatus(t, today);
      return t.context === contextId &&
        (status === TASK_STATUSES.TODAY || status === TASK_STATUSES.SCHEDULED);
    });
  }, [tasks]);

  // Get tasks by time block
  const getTasksByTimeBlock = useCallback((timeBlockId) => {
    return tasks.filter(t => t.timeBlockId === timeBlockId);
  }, [tasks]);

  // Get tasks by project
  const getTasksByProject = useCallback((projectId) => {
    return tasks.filter(t => t.projectId === projectId);
  }, [tasks]);

  // Get tasks by goal (direct, not through project)
  const getTasksByGoal = useCallback((goalId) => {
    return tasks.filter(t => t.goalId === goalId && !t.projectId);
  }, [tasks]);

  // Get overdue tasks (due date passed, not completed)
  const getOverdueTasks = useCallback(() => {
    return tasks.filter(t =>
      t.dueDate &&
      t.dueDate < getTodayString() &&
      !t.completedAt
    );
  }, [tasks]);

  // Quick add (straight to inbox - no properties set)
  const quickAdd = async (title) => {
    return await addTask({
      title,
      source: 'manual'
    });
  };

  // Assign task to a time block (auto-sets scheduledDate)
  const assignToBlock = async (id, blockId, blockName, scheduledDate = null) => {
    const updates = {
      gcalTimeBlockId: blockId,
      gcalTimeBlockName: blockName,
      isSomeday: false
    };
    if (scheduledDate) {
      updates.scheduledDate = scheduledDate;
    }
    await updateTask(id, updates);
  };

  // Remove task from time block
  const removeFromBlock = async (id) => {
    await updateTask(id, {
      gcalTimeBlockId: null,
      gcalTimeBlockName: null
    });
  };

  // Get unassigned tasks (today/scheduled without a time block)
  const getUnassignedTasks = useCallback(() => {
    const today = getTodayString();
    return tasks.filter(t => {
      const status = computeStatus(t, today);
      return (status === TASK_STATUSES.TODAY || status === TASK_STATUSES.SCHEDULED) &&
        !t.gcalTimeBlockId;
    });
  }, [tasks]);

  // Get unique delegates sorted by frequency (most used first)
  // Uses both legacy and new delegation fields
  const getUniqueDelegates = useCallback(() => {
    const delegateMap = new Map(); // name -> { name, uid?, email?, count }

    tasks.forEach(t => {
      // Check new fields first, then legacy
      const name = t.delegatedToName || t.delegatedTo;
      if (name && name.trim()) {
        const trimmedName = name.trim();
        if (delegateMap.has(trimmedName)) {
          const existing = delegateMap.get(trimmedName);
          existing.count++;
          // Update with UID/email if we have them and didn't before
          if (!existing.uid && t.delegatedToUid) existing.uid = t.delegatedToUid;
          if (!existing.email && t.delegatedToEmail) existing.email = t.delegatedToEmail;
        } else {
          delegateMap.set(trimmedName, {
            name: trimmedName,
            uid: t.delegatedToUid || null,
            email: t.delegatedToEmail || null,
            count: 1
          });
        }
      }
    });

    return [...delegateMap.values()].sort((a, b) => {
      const freqDiff = b.count - a.count;
      return freqDiff !== 0 ? freqDiff : a.name.localeCompare(b.name);
    });
  }, [tasks]);

  // Get tasks delegated BY current user (I delegated these to others)
  const getDelegatedByMeTasks = useCallback(() => {
    if (!user?.uid) return [];
    return tasks.filter(t =>
      (t.delegatedByUid === user.uid || t.userId === user.uid) &&
      isDelegated(t) &&
      !t.completedAt
    );
  }, [tasks, user?.uid]);

  // Get tasks delegated TO current user (others delegated to me)
  const getDelegatedToMeTasks = useCallback(() => {
    if (!user?.uid) return [];
    return tasks.filter(t =>
      t.delegatedToUid === user.uid &&
      !t.completedAt
    );
  }, [tasks, user?.uid]);

  // Get tasks with follow-up due today
  const getFollowUpDueToday = useCallback(() => {
    const today = getTodayString();
    return tasks.filter(t =>
      t.followUpDate === today &&
      isDelegated(t) &&
      !t.completedAt
    );
  }, [tasks]);

  return {
    tasks,
    loading,
    error,
    // CRUD
    addTask,
    updateTask,
    deleteTask,
    completeTask,
    uncompleteTask,
    quickAdd,
    // Action-based status changes (status computed from these properties)
    moveToInbox,
    moveToWaiting,
    moveToSomeday,
    scheduleTask,
    // Today focus
    addToToday,
    removeFromToday,
    setFollowUp,
    // New delegation methods
    delegateTask,
    updateDelegationStatus,
    removeDelegation,
    // Time block assignment
    assignToBlock,
    removeFromBlock,
    // Filters (using computed status)
    getInboxTasks,
    getTodayViewTasks,
    getWaitingTasks,
    getScheduledTasks,
    getSomedayTasks,
    getCompletedTasks,
    getTodayTasks,
    getTodayTasksFlat,
    getTasksByContext,
    getTasksByTimeBlock,
    getTasksByProject,
    getTasksByGoal,
    getOverdueTasks,
    getUnassignedTasks,
    getUniqueDelegates,
    // Delegation filters
    getDelegatedByMeTasks,
    getDelegatedToMeTasks,
    getFollowUpDueToday
  };
}
